import { WriteTransaction } from 'replicache'
import { WithServerSecret } from '@basehub/data/replicache/tx/spec'
import {
  AtLeast,
  CreateBlockParams,
  DeleteBlockParams,
  ReviveBlockParams,
  UpdateBlockParams,
} from './crud'
import { repoSpaceMutators } from '../../mutators'
import { ClearListCellsParams } from './list'
import {
  blockBaseOps,
  ensureBaseHubTransaction,
} from '@basehub/data/replicache/tx/iso'
import {
  BlockRelationship,
  Block,
  blockRelationshipBaseOps,
  branchBaseOps,
} from '@basehub/data/replicache/index'

export type BlockBatchMutationParams = {
  mutations: Array<
    | {
        op: 'deleteBlock'
        args: DeleteBlockParams
      }
    | {
        op: 'createBlock'
        args: CreateBlockParams
      }
    | {
        op: 'reviveBlock'
        args: ReviveBlockParams
      }
    | {
        op: 'updateBlock'
        args: UpdateBlockParams
      }
    | {
        op: 'clearListCells'
        args: ClearListCellsParams
      }
    | {
        op: 'updateBR'
        args: AtLeast<BlockRelationship, 'id'>
      }
    | {
        op: 'clearBranchAppliedAt'
        args: { branchId: string }
      }
    | {
        op: 'updateBlockRaw'
        args: AtLeast<Block, 'id'>
      }
  >
}

export const blockBatchMutations = async (
  tx: WriteTransaction,
  params: WithServerSecret<BlockBatchMutationParams>
) => {
  ensureBaseHubTransaction(tx)

  const blocksBeingDeleted = params.mutations.flatMap((m) => {
    if (m.op === 'deleteBlock') {
      return [m.args.id, ...(m.args.blocksAlsoBeingDeleted ?? [])]
    }
    return []
  })

  const undo: BlockBatchMutationParams['mutations'] = []

  for (const { op, args } of params.mutations) {
    let result
    switch (op) {
      case 'reviveBlock': {
        const mutator = repoSpaceMutators[op]
        result = await mutator(tx, args)
        break
      }
      case 'deleteBlock': {
        const mutator = repoSpaceMutators[op]
        result = await mutator(tx, {
          ...args,
          blocksAlsoBeingDeleted: blocksBeingDeleted,
        })
        break
      }
      case 'updateBlock': {
        const mutator = repoSpaceMutators[op]
        result = await mutator(tx, args)
        break
      }
      case 'createBlock': {
        const mutator = repoSpaceMutators[op]
        result = await mutator(tx, args)
        break
      }
      case 'clearListCells': {
        const mutator = repoSpaceMutators[op]
        result = await mutator(tx, args)
        break
      }
      case 'updateBR': {
        result = await blockRelationshipBaseOps.update(tx, args)
        break
      }
      case 'updateBlockRaw': {
        result = await blockBaseOps.update(tx, args)
        break
      }
      case 'clearBranchAppliedAt': {
        result = await branchBaseOps.update(tx, {
          id: args.branchId,
          inlineSuggestionAppliedAt: null,
        })
        break
      }
      default:
        throw new Error(`Op not yet implemented: ${op}`)
    }
    if (result && 'undo' in result) {
      undo.unshift(...result.undo)
    }
  }

  return { undo }
}
